import { triggerTracking } from '@zoopla/tracking';
import { ButtonLeft } from '@zoopla-eng/zds-components/button';
import { Divider } from '@zoopla-eng/zds-components/divider';
import { EmailMedium, SaveMedium } from '@zoopla-eng/zds-components/icons';
import { Link } from '@zoopla-eng/zds-components/link';
import { Stack } from '@zoopla-eng/zds-components/stack';
import { AnalyticsTaxonomy } from '@zoopla/listing-search-context';
import { useGetMapViewUrl } from '@zoopla/listing-search/use-map-view-url';
import { useIsLoggedIn, useUser } from '@zoopla/user';
import {
    ANALYTICS_BASE_TAXONOMY,
    Taxonomy,
    updateUserTaxonomyKeys,
    UserTaxonomy,
    useTrackSegmentEvent
} from '@zoopla/tracking';
import { FC, useRef } from 'react';
import {
    trackCreateEmailAlert,
    trackSaveThisSearch
} from './SaveThisSearchModal.tracking';
import { SaveThisSearchModalWithAuth } from './SaveThisSearchModalWithAuth';
import { useSaveThisSearchModal } from './useSaveThisSearchModal';
import { saveEmailAlertModalStyle } from './AlertPanel.styles.css';
import { useRedirectToTravelTime } from '../lib/utils';

type AlertPanelProps = {
    analyticsTaxonomy?: AnalyticsTaxonomy;
};

const signedOutLink = `/signin/?page_after_login=${encodeURIComponent(
    '/myaccount/alerts-searches/'
)}&return_url=${encodeURIComponent('/myaccount/alerts-searches/')}`;

export const AlertPanel: FC<React.PropsWithChildren<AlertPanelProps>> = ({
    analyticsTaxonomy
}) => {
    const isLoggedIn = useIsLoggedIn();

    const {
        showAuthModal,
        setShowAuthModal,
        defaultAlertFrequency,
        setDefaultAlertFrequency,
        showSaveThisSearchModal,
        setShowSaveThisSearchModal
    } = useSaveThisSearchModal();

    const drawYourSearchHref = useGetMapViewUrl(true);
    const redirectToTravelTime = useRedirectToTravelTime();

    const userTaxonomy = useUser();
    const trackSegmentEvent = useTrackSegmentEvent();

    const container = useRef<HTMLDivElement>(null);

    // this is needed because this page is still in Mason
    // and it does not redirect to signin with the correct params
    const savedSearchesLink = isLoggedIn
        ? '/myaccount/alerts-searches/'
        : signedOutLink;

    return (
        <div className={saveEmailAlertModalStyle} ref={container}>
            <Stack rowGap="S400">
                <ButtonLeft
                    aria-label="Create email alert"
                    category="secondary"
                    Icon={EmailMedium}
                    onClick={() => {
                        setDefaultAlertFrequency('0');

                        trackCreateEmailAlert();

                        trackSegmentEvent(
                            'Click Email Alert',
                            {
                                ...ANALYTICS_BASE_TAXONOMY,
                                ...(analyticsTaxonomy as unknown as Taxonomy),
                                ...(updateUserTaxonomyKeys(
                                    userTaxonomy
                                ) as unknown as UserTaxonomy),
                                signedInStatus: isLoggedIn
                                    ? 'signed in'
                                    : 'signed out',
                                location: analyticsTaxonomy?.areaName
                            },
                            undefined
                        );

                        if (!isLoggedIn) {
                            setShowAuthModal(true);
                        } else {
                            setShowSaveThisSearchModal(true);
                        }
                    }}
                >
                    Create email alert
                </ButtonLeft>

                <ButtonLeft
                    aria-label="Save this search"
                    category="secondary"
                    Icon={SaveMedium}
                    onClick={() => {
                        setDefaultAlertFrequency('0');

                        trackSaveThisSearch();

                        trackSegmentEvent(
                            'Click Save Search',
                            {
                                ...ANALYTICS_BASE_TAXONOMY,
                                ...(analyticsTaxonomy as unknown as Taxonomy),
                                ...(updateUserTaxonomyKeys(
                                    userTaxonomy
                                ) as unknown as UserTaxonomy),
                                signedInStatus: isLoggedIn
                                    ? 'signed in'
                                    : 'signed out',
                                location: analyticsTaxonomy?.areaName
                            },
                            undefined
                        );

                        if (!isLoggedIn) {
                            setShowAuthModal(true);
                        } else {
                            setShowSaveThisSearchModal(true);
                        }
                    }}
                >
                    Save this search
                </ButtonLeft>

                <Divider direction="horizontal" />
                <Link
                    onClick={redirectToTravelTime}
                    aria-label="Refine your search with estimated travel time"
                >
                    Travel time search
                </Link>
                {/* treat as single child for wrapping flex */}
                <div>
                    <Link
                        onClick={() => {
                            triggerTracking({
                                event: 'GAEvent',
                                eventCategory: 'search results',
                                eventAction: 'clickthrough',
                                eventLabel: 'draw_search'
                            });
                        }}
                        href={drawYourSearchHref}
                        aria-label="Refine your search area by drawing on the map"
                    >
                        Draw your search
                    </Link>
                    <SaveThisSearchModalWithAuth
                        {...{
                            showAuthModal,
                            setShowAuthModal,
                            defaultAlertFrequency,
                            setDefaultAlertFrequency,
                            showSaveThisSearchModal,
                            setShowSaveThisSearchModal,
                            container,
                            analyticsTaxonomy
                        }}
                    />
                </div>
                <div>
                    <Link
                        href={savedSearchesLink}
                        aria-label="Retrieve your previously saved searches and alerts"
                    >
                        Saved searches & alerts
                    </Link>
                </div>
            </Stack>
        </div>
    );
};
